// apps/admin/src/components/AggregatorLabelsGroup.tsx
// Displays all aggregator labels for a given upload mode.
// Used in OnboardingDataUploader to show which aggregators support each upload method.

import React from 'react';
import { AggregatorLabel, AggregatorName } from './AggregatorLabel';

/**
 * Upload mode to aggregator mapping.
 * Defines which aggregators support each upload method.
 *
 * Business Rules (derived from getTemplates.ts:42-50):
 * - CSV templates → ['paired', 'zipWithPairs'] (RCTI+CSV support)
 * - XLSX templates → ['legacy'] (ZIP Archive only)
 * - Entors has special useRidBasedPairing: true → auto-selects entorsPaired
 * - Loan Market has special multi-entity support → enables multiEntity
 */
const UPLOAD_MODE_AGGREGATORS: Record<string, AggregatorName[]> = {
  legacy: ['AFG', 'ENTORS', 'LOAN_MARKET', 'ENTICAL', 'ENTOVER', 'ENTOSSEY', 'WLTH', 'ORDE', 'ASTUTE', 'CONNECTIVE', 'VISION', 'CHOICE', 'LMG'],
  zipWithPairs: ['AFG'],
  entorsPaired: ['ENTORS'],
  multiEntity: ['LOAN_MARKET'],
};

interface AggregatorLabelsGroupProps {
  uploadMode: string;
  size?: 'sm' | 'md';
}

/**
 * Displays all aggregator labels for a given upload mode.
 * Automatically fetches the correct aggregators from the static mapping.
 */
export const AggregatorLabelsGroup: React.FC<AggregatorLabelsGroupProps> = ({
  uploadMode,
  size = 'sm',
}) => {
  const aggregators = UPLOAD_MODE_AGGREGATORS[uploadMode] || [];

  if (aggregators.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {aggregators.map((aggregator) => (
        <AggregatorLabel key={aggregator} aggregator={aggregator} size={size} />
      ))}
    </div>
  );
};

export default AggregatorLabelsGroup;
